import React from "react";
import Head from "next/head";
import { NextPage } from "next";
import Link from "next/link";

import LoadingPage from "../components/LoadingPage";
import useEnv from "../src/useEnv";
import useTenant from "../src/useTenant";
import useSessionStorage from "../components/get-started/useSessionStorage";

import OnboardingHeader from "../components/OnboardingHeader";
import OnboardingProgressBar from "../components/OnboardingProgressBar";
import { TerminologyPage } from "../components/get-started/TerminologyPage";
import { IntroPage } from "../components/get-started/IntroPage";
import { PolicyPage } from "../components/get-started/PolicyPage";
import { SummaryPage } from "../components/get-started/SummaryPage";
import { EnforcePage } from "../components/get-started/EnforcePage";
import { usePageNavigation } from "../components/get-started/usePageNavigation";
import { StarterPolicy } from "@/components/get-started/types";

const starterPolicy: StarterPolicy = {
  actor: "User",
  container: "Item",
  businessObject: "Item",
};

const pages = ["Intro", "Terminology", "Policy", "Enforce", "Summary"] as const;

const GetStartedPage: NextPage = () => {
  const { tenant } = useTenant();
  const { selectedEnv } = useEnv();

  const {
    currentPage,
    currentPageIndex,
    navigatePrevious,
    navigateNext,
    createPageLogger,
  } = usePageNavigation(pages);

  const [startTime] = useSessionStorage<string>(
    "get-started-startTime",
    new Date().toISOString()
  );

  if (!selectedEnv || !tenant) {
    return <LoadingPage />;
  }

  return (
    <>
      <Head>
        <title>Oso Cloud - Get Started</title>
      </Head>

      <div className="p-10">
        <div className="mx-auto max-w-[800px]">
          <OnboardingHeader />
        </div>

        <h1 className="font-space text-3xl text-center">
          Get Started with Oso Cloud
        </h1>

        <div className="mt-3" />
        <OnboardingProgressBar
          pages={pages}
          currentPageIndex={currentPageIndex}
        />

        <div className="mt-6" />

        {currentPage === "Intro" ? (
          <IntroPage
            policy={starterPolicy}
            onBack={navigatePrevious}
            onComplete={navigateNext}
          />
        ) : currentPage === "Terminology" ? (
          <TerminologyPage
            onComplete={navigateNext}
            onBack={navigatePrevious}
          />
        ) : currentPage === "Policy" ? (
          <PolicyPage
            selectedEnv={selectedEnv}
            policy={starterPolicy}
            onBack={navigatePrevious}
            onComplete={navigateNext}
            logAnalytics={createPageLogger("Policy")}
          />
        ) : currentPage === "Enforce" ? (
          <EnforcePage
            selectedEnv={selectedEnv}
            policy={starterPolicy}
            startTime={startTime}
            onBack={navigatePrevious}
            onComplete={navigateNext}
            logAnalytics={createPageLogger("Enforce")}
          />
        ) : currentPage === "Summary" ? (
          <SummaryPage
            onBack={navigatePrevious}
            logAnalytics={createPageLogger("Summary")}
          />
        ) : (
          <>{console.error("page not implemented", currentPage)}</>
        )}

        <Link href="/rules-editor" passHref>
          <a
            className="text-sm underline text-gray-500 fixed left-4 bottom-4"
            onClick={() => createPageLogger("Intro")("Skip")}
          >
            Skip this for now
          </a>
        </Link>
      </div>
    </>
  );
};

export default GetStartedPage;
